import { Component } from '@theme/component';

// https://developers.klaviyo.com/en/reference/create_client_subscription
const KLAVIYO_REVISION = '2026-04-15';
const KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions';

// TODO(dev): temporary testing bypass — remove before launch. Visiting the
// page with ?preview_mode skips the form and Klaviyo entirely and jumps
// straight to the success state, so the Kit Survey CTA (with name/email
// params) is immediately available. Override the identity with
// ?preview_mode&name=...&email=... if needed.
const TEST_BYPASS_PARAM = 'preview_mode';
const TEST_BYPASS_DEFAULTS = { name: 'Jake', email: 'jakeshw@gmail.com' };

/**
 * A quick-capture signup form. Talks to Klaviyo only — it creates a
 * Klaviyo profile (with email marketing consent) via Klaviyo's client
 * subscriptions API (requires a list ID), and Klaviyo's Shopify integration syncs that profile
 * back to Shopify as a customer. No Shopify customer form is involved,
 * which also means no hCaptcha wiring to fight with. Deliberately simple
 * (name/email/phone only) — the address lives on the Kit Survey instead.
 *
 * Unlike Shopify's customer form, Klaviyo returns a real status code, so
 * success/error UI here is accurate: the success message (and Kit Survey
 * CTA) only shows on a 202, and anything else surfaces the error message
 * with Klaviyo's detail when available.
 *
 * If a Kit Survey URL is configured (`data-kit-survey-url`), the success
 * message reveals a CTA link to that page, with the visitor's name/email
 * appended as URL parameters so the survey page can greet them and attach
 * its own Klaviyo write to the same identity.
 *
 * @typedef {Object} InterestSignupFormRefs
 * @property {HTMLInputElement} emailInput
 * @property {HTMLInputElement} [firstNameInput]
 * @property {HTMLInputElement} [lastNameInput]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} successMessageText
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} errorMessageText
 * @property {HTMLAnchorElement} [kitSurveyLink]
 *
 * @extends {Component<InterestSignupFormRefs>}
 */
class InterestSignupForm extends Component {
  requiredRefs = ['emailInput', 'submitButton', 'successMessage', 'successMessageText', 'errorMessage', 'errorMessageText'];

  connectedCallback() {
    super.connectedCallback();
    this.#maybeBypassForTesting();
  }

  /**
   * TODO(dev): temporary testing bypass — remove before launch (along with
   * the constants above and the connectedCallback hook). Shows the success
   * state without touching Klaviyo when `?preview_mode` is in the URL.
   */
  #maybeBypassForTesting() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(TEST_BYPASS_PARAM)) return;

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    this.#showSuccess(form, {
      name: params.get('name') || TEST_BYPASS_DEFAULTS.name,
      email: params.get('email') || TEST_BYPASS_DEFAULTS.email,
    });
  }

  /**
   * Bound to the wrapping element via `on:submit="/handleSubmit"`. Submit
   * events bubble from the inner `<form>` up to this custom element.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const normalizedPhone = this.#normalizePhone(this.refs.phoneInput);
    if (!form.reportValidity()) return;

    const publicApiKey = this.dataset.klaviyoPublicApiKey;
    if (!publicApiKey) {
      console.error('interest-signup-form: missing a Klaviyo public API key. Set it in Theme settings → Klaviyo.');
      this.#showError();
      return;
    }

    const listId = this.dataset.klaviyoListId;
    if (!listId) {
      console.error('interest-signup-form: missing a Klaviyo list ID. Set it on this block or under Theme settings → Klaviyo.');
      this.#showError();
      return;
    }

    const { emailInput, firstNameInput, lastNameInput, submitButton, errorMessage } = this.refs;

    errorMessage.classList.add('hidden');
    submitButton.disabled = true;

    /** @type {Record<string, unknown>} */
    const profileAttributes = {
      email: emailInput.value,
      subscriptions: {
        email: {
          marketing: { consent: 'SUBSCRIBED' },
        },
      },
    };

    const firstName = firstNameInput instanceof HTMLInputElement ? firstNameInput.value.trim() : '';
    if (firstName) profileAttributes.first_name = firstName;

    const lastName = lastNameInput instanceof HTMLInputElement ? lastNameInput.value.trim() : '';
    if (lastName) profileAttributes.last_name = lastName;

    if (normalizedPhone) profileAttributes.phone_number = normalizedPhone;

    /** @type {Record<string, unknown>} */
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Kit Interest Form',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId },
          },
        },
      },
    };

    try {
      const response = await fetch(`${KLAVIYO_SUBSCRIBE_URL}?company_id=${encodeURIComponent(publicApiKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });

      if (response.status === 202) {
        this.#showSuccess(form, { name: firstName, email: emailInput.value });
        return;
      }

      this.#showError(await this.#parseErrorDetail(response));
    } catch (error) {
      console.error('interest-signup-form: Klaviyo subscribe request failed.', error);
      this.#showError();
    } finally {
      submitButton.disabled = false;
    }
  };

  /**
   * Bound to the phone input via `on:input="/handlePhoneInput"`. A custom
   * validity message set by #normalizePhone() on a failed submit persists
   * on the field until explicitly cleared — the browser does not clear it
   * just because the value changed, and while it's set, the browser blocks
   * the native submit (and therefore this component's `submit` handler)
   * before it can run again. Clearing it as the visitor edits the field is
   * what lets a corrected phone number actually be submittable.
   *
   * @param {Event} event
   */
  handlePhoneInput = (event) => {
    if (event.target instanceof HTMLInputElement) event.target.setCustomValidity('');
  };

  /**
   * Klaviyo requires phone numbers in E.164 (a leading "+" then country
   * code, e.g. +15555550123) and rejects the whole profile write — email
   * subscription included — if the phone attribute doesn't match. Visitors
   * overwhelmingly just type a bare domestic number, so normalize the
   * common US shapes here rather than let an untouched-looking phone field
   * silently sink the signup. Anything else gets flagged as a field-level
   * validation error via the input's own reportValidity(), instead of
   * reaching the network at all.
   *
   * @param {HTMLInputElement} [phoneInput]
   * @returns {string} value to send to Klaviyo, or '' if the field is blank
   */
  #normalizePhone(phoneInput) {
    if (!(phoneInput instanceof HTMLInputElement)) return '';

    phoneInput.setCustomValidity('');
    const raw = phoneInput.value.trim();
    if (!raw) return '';

    const withPlus = raw.replace(/[^\d+]/g, '');
    if (/^\+[1-9]\d{7,14}$/.test(withPlus)) return withPlus;

    const digitsOnly = withPlus.replace(/\D/g, '');
    if (digitsOnly.length === 10) return `+1${digitsOnly}`;
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) return `+${digitsOnly}`;

    phoneInput.setCustomValidity('Enter your phone number with a country code, e.g. +1 555 555 5555.');
    return raw;
  }

  /**
   * Full pane change: hides the form AND every sibling block in the same
   * group (eyebrow, heading, description, disclaimer text blocks), so the
   * success pane stands alone.
   *
   * @param {HTMLFormElement} form
   * @param {{ name: string, email: string }} visitor
   */
  #showSuccess(form, { name, email }) {
    const { successMessage, successMessageText, kitSurveyLink } = this.refs;

    this.#pointKitSurveyLink({ name, email, kitSurveyLink });

    const template = successMessageText.dataset.successTemplate || successMessageText.textContent || '';
    successMessageText.textContent = name
      ? template.replaceAll('[name]', name)
      : template.replace(/,? \[name\]/, '').replace('[name]', '').trim();

    for (const sibling of this.parentElement?.children ?? []) {
      if (sibling !== this) sibling.classList.add('hidden');
    }

    form.classList.add('hidden');
    successMessage.classList.remove('hidden');
    successMessage.focus();
  }

  /**
   * Points the Kit Survey CTA at the configured URL with the visitor's
   * name/email appended as query params, so that page can greet them and
   * attach its own Klaviyo write to the same identity.
   *
   * @param {{ name: string, email: string, kitSurveyLink: HTMLAnchorElement | undefined }} args
   */
  #pointKitSurveyLink({ name, email, kitSurveyLink }) {
    if (!(kitSurveyLink instanceof HTMLAnchorElement)) return;

    const kitSurveyUrl = this.dataset.kitSurveyUrl;
    if (!kitSurveyUrl) return;

    const url = new URL(kitSurveyUrl, window.location.origin);
    if (name) url.searchParams.set('name', name);
    if (email) url.searchParams.set('email', email);
    kitSurveyLink.href = `${url.pathname}${url.search}`;
    kitSurveyLink.classList.remove('hidden');
  }

  /**
   * @param {Response} response
   * @returns {Promise<string | undefined>}
   */
  async #parseErrorDetail(response) {
    try {
      const data = await response.json();
      return data?.errors?.[0]?.detail;
    } catch {
      return undefined;
    }
  }

  /** @param {string} [detail] */
  #showError(detail) {
    const { errorMessage, errorMessageText } = this.refs;
    errorMessageText.textContent = detail || errorMessageText.dataset.defaultMessage || errorMessageText.textContent;
    errorMessage.classList.remove('hidden');
    errorMessage.focus();
  }
}

if (!customElements.get('interest-signup-form')) {
  customElements.define('interest-signup-form', InterestSignupForm);
}
